import { buildBracketPages, countBracketTeams, type BracketPage } from './bracketPaging';
import { slotOriginLabel } from './printLabels';
import type { CompetitionBracketData, CompetitionMatch } from '../../../types';

/** Renders the full set of A4 bracket sheets for a competition. */
export function BracketPages({
  tournamentName,
  bracket,
}: {
  tournamentName: string;
  bracket: CompetitionBracketData;
}) {
  const pages = buildBracketPages(bracket);
  if (pages.length === 0) return null;

  const teamCount = countBracketTeams(bracket);

  return (
    <>
      {pages.map((page, i) => (
        <BracketSheet
          key={page.key}
          page={page}
          tournamentName={tournamentName}
          teamCount={teamCount}
          folha={i + 1}
          total={pages.length}
        />
      ))}
    </>
  );
}

function BracketSheet({
  page,
  tournamentName,
  teamCount,
  folha,
  total,
}: {
  page: BracketPage;
  tournamentName: string;
  teamCount: number;
  folha: number;
  total: number;
}) {
  const firstColCount = page.columns[0]?.matches.length ?? 1;
  // Keep each tree tall enough for the connectors to line up while still
  // fitting one A4 portrait page (footer pages reserve room for the footer).
  const minHeight = page.footer
    ? Math.min(firstColCount * 170, 640)
    : Math.min(firstColCount * 200, 840);

  return (
    <section className="sheet break">
      <header className="page-head">
        <div className="title">
          <small>{tournamentName} · Mata-mata</small>
          {page.title}
        </div>
        <div className="meta">
          <div>
            <b>Eliminação simples{teamCount > 0 ? ` · ${teamCount} duplas` : ''}</b>
          </div>
          <div>Formato: set único · até 6 games</div>
          <div>Tiebreak de 7 pontos em 6-6</div>
          {total > 1 && (
            <div className="muted" style={{ marginTop: 4 }}>
              Folha {folha} de {total}
            </div>
          )}
        </div>
      </header>

      <div className="bracket-sub">{page.subtitle}</div>

      <div className="round-titles">
        {page.columns.map((col) => (
          <div className="rt" key={col.round}>
            {col.label}
          </div>
        ))}
      </div>

      <div className="bracket" style={{ minHeight }}>
        {page.columns.map((col, ci) => {
          const isLastColumn = ci === page.columns.length - 1;
          return (
            <div className="round" key={col.round}>
              {col.matches.map((match) => (
                <div className="match" key={match.id}>
                  <BracketCard match={match} roundSuffix={isLastColumn ? col.label : undefined} />
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {page.footer && (
        <div className="bracket-footer">
          {page.footer.thirdPlace && (
            <div className="third-place">
              <div className="round-head">
                <span className="round-name">Disputa de 3º lugar</span>
                <span className="round-sub">1 jogo</span>
              </div>
              <div className="match-card">
                <div className="jogo">3º lugar</div>
                <BracketSlot match={page.footer.thirdPlace} slot={1} />
                <BracketSlot match={page.footer.thirdPlace} slot={2} />
              </div>
            </div>
          )}
          <div className="champion">
            <div className="lbl">Campeão</div>
            <div className="line">
              <span className="rule" />
            </div>
          </div>
        </div>
      )}

      <div className="foot-note">
        <span>Escreva o nome da dupla na linha; o rótulo abaixo indica a origem da vaga</span>
        <span>{tournamentName}</span>
      </div>
    </section>
  );
}

/** A "Jogo N" card with its two blank write-line slots. */
function BracketCard({ match, roundSuffix }: { match: CompetitionMatch; roundSuffix?: string }) {
  const jogo = match.gameNumber != null ? `Jogo ${match.gameNumber}` : 'Jogo';
  return (
    <div className="match-card">
      <div className="jogo">
        {jogo}
        {roundSuffix ? ` · ${roundSuffix}` : ''}
      </div>
      <BracketSlot match={match} slot={1} />
      <BracketSlot match={match} slot={2} />
    </div>
  );
}

/** One slot of a bracket match: a blank write-line with its origin label below. */
function BracketSlot({ match, slot }: { match: CompetitionMatch; slot: 1 | 2 }) {
  const source = slot === 1 ? match.team1Source : match.team2Source;
  const team = slot === 1 ? match.team1 : match.team2;
  return (
    <div className="bslot">
      <div className="write">
        <div className="wline" />
        <div className="blabel">{slotOriginLabel(source, team)}</div>
      </div>
      <span className="box bx" />
    </div>
  );
}
